/**
 * RoleMorphic
 *
 * Motor de conversão polimórfica de valores.
 * Permite que um valor assuma múltiplas formas (variantes) mantendo sua identidade semântica.
 *
 * Arquitetura Hub-and-Spoke:
 * - Cada role tem uma variante BASE que serve como pivot
 * - Toda conversão passa pela base: origem -> base -> destino
 * - Isso reduz a complexidade de N² para 2N conversões
 */

import {
  RoleSpec,
  VariantSpec,
  ParsedVariantId,
  ConversionResult,
  ReversibilityResult,
  MorpherFn,
  MorpherSpec,
} from "./types";

export class RoleMorphic {
  /** Registry de roles */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private roles = new Map<string, RoleSpec<any>>();

  /** Registry de morphers (transformações cross-role) */
  private morphers = new Map<string, MorpherFn>();

  // ===========================================================================
  // ROLE MANAGEMENT
  // ===========================================================================

  /**
   * Registra uma nova role
   */
  register<TBase = unknown>(roleId: string, spec: RoleSpec<TBase>): this {
    if (this.roles.has(roleId)) {
      throw new Error(`Role "${roleId}" is already registered`);
    }

    // Valida que a base existe nas variantes
    if (!spec.variants[spec.base]) {
      throw new Error(
        `Base variant "${spec.base}" not found in role "${roleId}" variants`,
      );
    }

    this.roles.set(roleId, spec);
    return this;
  }

  /**
   * Remove uma role registrada
   */
  unregister(roleId: string): boolean {
    return this.roles.delete(roleId);
  }

  /**
   * Verifica se uma role está registrada
   */
  hasRole(roleId: string): boolean {
    return this.roles.has(roleId);
  }

  /**
   * Obtém a spec de uma role
   */
  getRole(roleId: string): RoleSpec | undefined {
    return this.roles.get(roleId);
  }

  /**
   * Lista todas as roles registradas
   */
  listRoles(): string[] {
    return Array.from(this.roles.keys());
  }

  // ===========================================================================
  // VARIANT UTILITIES
  // ===========================================================================

  /**
   * Parseia um identificador de variante
   *
   * "color:rgb:object" => { role: "color", variant: "rgb:object" }
   * "area:m2" => { role: "area", variant: "m2" }
   */
  parseVariantId(fullId: string): ParsedVariantId {
    const colonIndex = fullId.indexOf(":");
    if (colonIndex === -1) {
      throw new Error(
        `Invalid variant identifier "${fullId}". Expected format: "role:variant"`,
      );
    }

    return {
      role: fullId.slice(0, colonIndex),
      variant: fullId.slice(colonIndex + 1),
    };
  }

  /**
   * Verifica se uma variante existe
   */
  hasVariant(fullId: string): boolean {
    try {
      const { role, variant } = this.parseVariantId(fullId);
      const spec = this.roles.get(role);
      return spec ? variant in spec.variants : false;
    } catch {
      return false;
    }
  }

  /**
   * Obtém a spec de uma variante
   */
  getVariant(fullId: string): VariantSpec | undefined {
    const { role, variant } = this.parseVariantId(fullId);
    const spec = this.roles.get(role);
    return spec?.variants[variant];
  }

  /**
   * Lista todas as variantes de uma role
   */
  listVariants(roleId: string): string[] {
    const spec = this.roles.get(roleId);
    if (!spec) return [];
    return Object.keys(spec.variants);
  }

  /**
   * Lista variantes para as quais uma variante pode converter
   */
  getConvertibleVariants(fullId: string): string[] {
    const { role, variant } = this.parseVariantId(fullId);
    const spec = this.roles.get(role);

    if (!spec) return [];

    return Object.keys(spec.variants)
      .filter((v) => v !== variant)
      .map((v) => `${role}:${v}`);
  }

  // ===========================================================================
  // CONVERSION
  // ===========================================================================

  /**
   * Converte um valor de uma variante para outra (mesma role)
   *
   * @param from - Identificador da variante de origem (ex: "color:hex")
   * @param to - Identificador da variante de destino (ex: "color:rgb:object")
   * @param value - Valor a converter
   * @returns Valor convertido
   * @throws Error se a conversão não for possível
   */
  convert<TFrom = unknown, TTo = unknown>(
    from: string,
    to: string,
    value: TFrom,
  ): TTo {
    const fromParsed = this.parseVariantId(from);
    const toParsed = this.parseVariantId(to);

    // Validação: mesma role
    if (fromParsed.role !== toParsed.role) {
      throw new Error(
        `Cannot convert between different roles ("${fromParsed.role}" -> "${toParsed.role}"). ` +
          `Use morph() for cross-role transformations.`,
      );
    }

    const roleSpec = this.roles.get(fromParsed.role);
    if (!roleSpec) {
      throw new Error(`Role "${fromParsed.role}" is not registered`);
    }

    const fromVariant = roleSpec.variants[fromParsed.variant];
    const toVariant = roleSpec.variants[toParsed.variant];

    if (!fromVariant) {
      throw new Error(
        `Variant "${fromParsed.variant}" not found in role "${fromParsed.role}"`,
      );
    }

    if (!toVariant) {
      throw new Error(
        `Variant "${toParsed.variant}" not found in role "${fromParsed.role}"`,
      );
    }

    // Otimização: se origem === destino, retorna o valor
    if (fromParsed.variant === toParsed.variant) {
      return value as unknown as TTo;
    }

    // Conversão via base (hub-and-spoke)
    const baseVariant = roleSpec.base;

    // Se origem é a base, pula toBase
    const baseValue =
      fromParsed.variant === baseVariant ? value : fromVariant.toBase(value);

    // Se destino é a base, retorna baseValue
    if (toParsed.variant === baseVariant) {
      return baseValue as TTo;
    }

    // Converte da base para o destino
    return toVariant.fromBase(baseValue) as TTo;
  }

  /**
   * Tenta converter, retornando Result ao invés de throw
   */
  tryConvert<TFrom = unknown, TTo = unknown>(
    from: string,
    to: string,
    value: TFrom,
  ): ConversionResult<TTo> {
    try {
      const result = this.convert<TFrom, TTo>(from, to, value);
      return { ok: true, value: result };
    } catch (error) {
      return {
        ok: false,
        error: error instanceof Error ? error.message : String(error),
      };
    }
  }

  // ===========================================================================
  // SAFE CONVERSION (with reversibility check)
  // ===========================================================================

  /**
   * Verifica se uma conversão é reversível (sem perda de dados)
   */
  isReversible<T = unknown>(from: string, to: string, value: T): boolean {
    try {
      const converted = this.convert(from, to, value);
      const reconverted = this.convert(to, from, converted);

      // Compara via JSON (deep equality simples)
      return JSON.stringify(value) === JSON.stringify(reconverted);
    } catch {
      return false;
    }
  }

  /**
   * Retorna detalhes sobre a reversibilidade de uma conversão
   */
  checkReversibility<T = unknown>(
    from: string,
    to: string,
    value: T,
  ): ReversibilityResult {
    try {
      const converted = this.convert(from, to, value);
      const reconverted = this.convert(to, from, converted);
      const reversible = JSON.stringify(value) === JSON.stringify(reconverted);

      return {
        reversible,
        original: value,
        converted,
        reconverted,
      };
    } catch (error) {
      return {
        reversible: false,
        original: value,
        converted: null,
        reconverted: null,
      };
    }
  }

  /**
   * Converte apenas se a conversão for reversível
   *
   * @throws Error se a conversão causar perda de dados
   */
  convertSafe<TFrom = unknown, TTo = unknown>(
    from: string,
    to: string,
    value: TFrom,
  ): TTo {
    const result = this.checkReversibility(from, to, value);

    if (!result.reversible) {
      throw new Error(
        `Conversion from "${from}" to "${to}" is not reversible. ` +
          `Original: ${JSON.stringify(value)}, ` +
          `Reconverted: ${JSON.stringify(result.reconverted)}`,
      );
    }

    return result.converted as TTo;
  }

  // ===========================================================================
  // MORPHERS (cross-role transformations)
  // ===========================================================================

  /**
   * Registra um morpher para transformação entre roles diferentes
   */
  registerMorpher<TFrom = unknown, TTo = unknown>(
    spec: MorpherSpec<TFrom, TTo>,
  ): this {
    const key = `${spec.from}->${spec.to}`;

    if (this.morphers.has(key)) {
      throw new Error(`Morpher "${key}" is already registered`);
    }

    this.morphers.set(key, spec.transform as MorpherFn);
    return this;
  }

  /**
   * Remove um morpher
   */
  unregisterMorpher(from: string, to: string): boolean {
    return this.morphers.delete(`${from}->${to}`);
  }

  /**
   * Verifica se existe um morpher para a transformação
   */
  hasMorpher(from: string, to: string): boolean {
    return this.morphers.has(`${from}->${to}`);
  }

  /**
   * Lista morphers disponíveis a partir de uma variante
   */
  getMorphersFrom(from: string): string[] {
    const prefix = `${from}->`;
    const targets: string[] = [];

    for (const key of this.morphers.keys()) {
      if (key.startsWith(prefix)) {
        targets.push(key.slice(prefix.length));
      }
    }

    return targets;
  }

  /**
   * Transforma um valor de uma role para outra usando um morpher registrado
   */
  morph<TFrom = unknown, TTo = unknown>(
    from: string,
    to: string,
    value: TFrom,
  ): TTo {
    const key = `${from}->${to}`;
    const morpher = this.morphers.get(key);

    if (!morpher) {
      throw new Error(
        `No morpher registered for "${from}" -> "${to}". ` +
          `Register one with registerMorpher().`,
      );
    }

    return morpher(value) as TTo;
  }

  /**
   * Tenta transformar, retornando Result ao invés de throw
   */
  tryMorph<TFrom = unknown, TTo = unknown>(
    from: string,
    to: string,
    value: TFrom,
  ): ConversionResult<TTo> {
    try {
      const result = this.morph<TFrom, TTo>(from, to, value);
      return { ok: true, value: result };
    } catch (error) {
      return {
        ok: false,
        error: error instanceof Error ? error.message : String(error),
      };
    }
  }

  // ===========================================================================
  // CONVENIENCE METHODS
  // ===========================================================================

  /**
   * Converte ou transforma automaticamente
   *
   * - Se mesma role: usa convert()
   * - Se roles diferentes: usa morph()
   */
  auto<TFrom = unknown, TTo = unknown>(
    from: string,
    to: string,
    value: TFrom,
  ): TTo {
    const fromParsed = this.parseVariantId(from);
    const toParsed = this.parseVariantId(to);

    if (fromParsed.role === toParsed.role) {
      return this.convert<TFrom, TTo>(from, to, value);
    }

    return this.morph<TFrom, TTo>(from, to, value);
  }

  /**
   * Converte um valor para a variante base da sua role
   */
  toBase<T = unknown, TBase = unknown>(fullId: string, value: T): TBase {
    const { role, variant } = this.parseVariantId(fullId);
    const spec = this.roles.get(role);

    if (!spec) {
      throw new Error(`Role "${role}" is not registered`);
    }

    // Se já é a base, retorna direto
    if (variant === spec.base) {
      return value as unknown as TBase;
    }

    const variantSpec = spec.variants[variant];
    if (!variantSpec) {
      throw new Error(`Variant "${variant}" not found in role "${role}"`);
    }

    return variantSpec.toBase(value) as TBase;
  }

  /**
   * Converte um valor da base para uma variante específica
   */
  fromBase<TBase = unknown, T = unknown>(fullId: string, baseValue: TBase): T {
    const { role, variant } = this.parseVariantId(fullId);
    const spec = this.roles.get(role);

    if (!spec) {
      throw new Error(`Role "${role}" is not registered`);
    }

    // Se é a base, retorna direto
    if (variant === spec.base) {
      return baseValue as unknown as T;
    }

    const variantSpec = spec.variants[variant];
    if (!variantSpec) {
      throw new Error(`Variant "${variant}" not found in role "${role}"`);
    }

    return variantSpec.fromBase(baseValue) as T;
  }
}
